import { useEffect, useState } from 'react'
import { Card, Typography, Form, Input, Button, message, Descriptions, Space, Popconfirm } from 'antd'
import { CopyOutlined, ReloadOutlined, EyeOutlined, EyeInvisibleOutlined, PlusOutlined } from '@ant-design/icons'
import { useMerchantStore } from '../../stores/merchantStore'
import { getMerchant, updateWebhook, regenerateWebhookSecret } from '../../services/merchant'

const { Title, Text, Paragraph } = Typography

export default function Webhooks() {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const { merchant, setMerchant } = useMerchantStore()
  const [form] = Form.useForm()

  const [showSecret, setShowSecret] = useState(false)
  const [webhookSecret, setWebhookSecret] = useState<string | null>(null)
  const [totpCode, setTotpCode] = useState('')

  useEffect(() => {
    setLoading(true)
    getMerchant()
      .then((res) => {
        setMerchant(res.data)
        form.setFieldsValue({ webhookUrl: res.data.webhookUrl })
      })
      .finally(() => setLoading(false))
  }, [form, setMerchant])

  const hasSecret = merchant?.hasWebhookSecret || webhookSecret !== null

  const handleSubmit = async (values: { webhookUrl: string }) => {
    setSaving(true)
    try {
      const res = await updateWebhook(values.webhookUrl, totpCode || undefined)
      if (res.data.webhookSecret) {
        setWebhookSecret(res.data.webhookSecret)
        setShowSecret(true)
        message.success('Webhook URL 已更新，Secret 已生成')
      } else {
        message.success('Webhook URL 已更新')
      }
      getMerchant().then((r) => setMerchant(r.data))
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '更新失敗')
    } finally {
      setSaving(false)
    }
  }

  const handleCopySecret = () => {
    if (webhookSecret) {
      navigator.clipboard.writeText(webhookSecret)
      message.success('已複製到剪貼板')
    }
  }

  const handleGenerateSecret = async () => {
    setRegenerating(true)
    try {
      const res = await regenerateWebhookSecret(totpCode || undefined)
      setWebhookSecret(res.data.secret)
      setShowSecret(true)
      getMerchant().then((r) => setMerchant(r.data))
      message.success(hasSecret ? 'Webhook Secret 已重新生成' : 'Webhook Secret 已生成')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '操作失敗')
    } finally {
      setRegenerating(false)
    }
  }

  const secretButton = (
    <Button
      icon={hasSecret ? <ReloadOutlined /> : <PlusOutlined />}
      loading={regenerating}
      onClick={hasSecret ? undefined : handleGenerateSecret}
    >
      {hasSecret ? '重新生成' : '生成'}
    </Button>
  )

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>Webhooks</Title>

      <Card title="Webhook 設定" style={{ marginBottom: 24 }} loading={loading}>
        <Paragraph>
          當訂單、退款或訂閱生命週期發生已確認的業務事件時，我們會向您設定的 Webhook URL 發送 HTTP POST 通知。
        </Paragraph>
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ maxWidth: 500 }}>
          <Form.Item
            name="webhookUrl"
            label="Webhook URL"
            rules={[
              { required: true, message: '請輸入 Webhook URL' },
              { pattern: /^https:\/\//, message: 'Webhook URL 必須使用 HTTPS' }
            ]}
          >
            <Input placeholder="https://your-domain.com/webhooks/kyren" />
          </Form.Item>
          {merchant?.twoFactorEnabled && (
            <Form.Item label="2FA 驗證碼">
              <Input value={totpCode} onChange={(event) => setTotpCode(event.target.value)} maxLength={6} />
            </Form.Item>
          )}
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>儲存</Button>
          </Form.Item>
        </Form>
      </Card>

      <Card title="Webhook 簽名驗證" style={{ marginBottom: 24 }}>
        <Paragraph>
          每個 Webhook 請求都包含簽名頭，用於驗證請求來自啟潤支付。
        </Paragraph>
        <Descriptions column={1} bordered>
          <Descriptions.Item label="簽名頭">X-Kyren-Signature</Descriptions.Item>
          <Descriptions.Item label="時間戳頭">X-Kyren-Timestamp</Descriptions.Item>
          <Descriptions.Item label="Webhook Secret">
            {merchant?.webhookUrl ? (
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  value={webhookSecret ? (showSecret ? webhookSecret : '••••••••••••••••••••••••') : (hasSecret ? 'whsec_••••••••••••••••' : '')}
                  readOnly
                  placeholder={hasSecret ? undefined : '尚未生成 Webhook Secret'}
                  style={{ fontFamily: 'monospace', flex: 1 }}
                />
                {webhookSecret && (
                  <Button
                    icon={showSecret ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    onClick={() => setShowSecret(!showSecret)}
                  />
                )}
                {webhookSecret && (
                  <Button icon={<CopyOutlined />} onClick={handleCopySecret} />
                )}
                {hasSecret ? (
                  <Popconfirm
                    title="確定要重新生成 Webhook Secret 嗎？"
                    description="舊的 Secret 將立即失效，您需要更新服務端設定。"
                    onConfirm={handleGenerateSecret}
                  >
                    {secretButton}
                  </Popconfirm>
                ) : (
                  secretButton
                )}
              </Space.Compact>
            ) : (
              <Text type="secondary">設定 Webhook URL 後可生成</Text>
            )}
          </Descriptions.Item>
        </Descriptions>
        <Paragraph style={{ marginTop: 16 }}>
          簽名輸入為 <Text code>{'${timestamp}.${rawBody}'}</Text>，簽名格式為{' '}
          <Text code>sha256=&lt;hex-hmac-sha256&gt;</Text>。請使用收到的 raw body 原始內容驗簽，
          <Text code>X-Kyren-Timestamp</Text> 為毫秒時間戳。
        </Paragraph>
      </Card>

      <Card title="支援的事件" style={{ marginBottom: 24 }}>
        <Descriptions column={1} bordered>
          <Descriptions.Item label="subscription.created">首期扣款成功並啟用訂閱</Descriptions.Item>
          <Descriptions.Item label="subscription.cancelled">訂閱實際進入已取消終態</Descriptions.Item>
          <Descriptions.Item label="subscription.terminated">訂閱不可恢復地終止</Descriptions.Item>
          <Descriptions.Item label="invoice.paid">某一期訂閱賬單扣款成功</Descriptions.Item>
          <Descriptions.Item label="invoice.payment_failed">某一期訂閱賬單最終扣款失敗</Descriptions.Item>
          <Descriptions.Item label="order.paid">訂單支付成功</Descriptions.Item>
          <Descriptions.Item label="order.refunded">訂單退款成功</Descriptions.Item>
          <Descriptions.Item label="order.closed">未支付訂單已關閉</Descriptions.Item>
        </Descriptions>
        <Paragraph style={{ marginTop: 16 }}>
          每個 payload 都包含 <Text code>id</Text>、<Text code>type</Text>、
          <Text code>created_at</Text> 和 <Text code>data</Text>。任意 2xx 響應視為 ACK；
          單次請求超時為 30 秒，最多 7 次總投遞。投遞語義為 at-least-once，
          接收方必須按 payload id 去重。
        </Paragraph>
      </Card>
    </div>
  )
}
